<template>
  <div class="hardware-faults">
    <v-tabs v-model="tab" align-with-title>
      <v-tabs-slider color="secondary"></v-tabs-slider>
      <v-tab v-for="page in faultPages" :key="page">
        {{ page }}
      </v-tab>
    </v-tabs>
    <v-row align="center device-control-bar">
      <v-col cols="2">
        <v-select
          v-model="deviceType"
          :items="types"
          label="Device Type"
          item-text="name"
          outlined
        />
      </v-col>
      <v-col v-if="displayTable('Per - Org')" cols="2">
        <v-autocomplete
          v-model="selectedOrg"
          :items="orgName"
          label="Organisations"
          item-text="name"
          outlined
          @change="getFaultsWrapper"
        />
      </v-col>

      <v-col v-if="displayTable('Daily (All Orgs)')" cols="3">
        <material-date-time-range
          :start-value="startValue"
          :local-time="true"
          :current-date-only="true"
          @singleDateSelected="(date) => (selectedSingleDate = date)"
        >
        </material-date-time-range>
      </v-col>

      <v-card-title class="faults-search-bar">
        <div style="display: flex; align-items: center">
          <div style="width: 250px">
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </div>

          <div style="margin-left: 10px">
            <v-chip-group
              v-if="deviceType == 'Camera System'"
              v-model="selectedFilter"
              multiple
              active-class="third--text"
              class="ma-4"
            >
              <v-chip v-for="tag in filters" :key="tag">
                {{ filterMap(tag) }}
              </v-chip>
            </v-chip-group>

            <v-chip-group
              v-else
              v-model="selectedFilter"
              multiple
              active-class="third--text"
              class="ma-4"
            >
              <v-chip v-for="(tag, index) in assetFilters" :key="index">
                {{ filterMap(tag.label) }}
              </v-chip>
            </v-chip-group>
          </div>

          <TableRefresh :state="loading" @click.native="refreshWrapper" />

          <div class="download-container">
            <v-icon class="download-icon" @click="downloadFile">
              mdi-download
            </v-icon>
          </div>
        </div>
      </v-card-title>
    </v-row>

    <v-data-table
      v-if="displayTable('Per - Org') && deviceType == 'Camera System'"
      :headers="headers.org[deviceType]"
      :items="filteredItems"
      :loading="loading"
      :search="search"
      :footer-props="{
        itemsPerPageOptions: [25, 50, 100, -1],
      }"
      :items-per-page="-1"
      class="elevation-1"
    >
      <template v-slot:progress>
        <v-progress-linear
          color="blue"
          height="2"
          indeterminate
        ></v-progress-linear>
      </template>

      <template v-slot:item.name="{ item }">{{ item.vehicle_id }}</template>

      <template v-slot:item.storage="{ item }">
        <v-icon
          v-if="faults.storage.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.camera="{ item }">
        <v-icon
          v-if="faults.camera.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.recording="{ item }">
        <v-icon
          v-if="faults.recording.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.gps="{ item }">
        <v-icon
          v-if="faults.gps.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.network="{ item }">
        <v-icon
          v-if="faults.status.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.desc="{ item }">{{
        faultDescription(item.faults)
      }}</template>
    </v-data-table>

    <v-data-table
      v-if="displayTable('Per - Org') && deviceType == 'Asset Tracker'"
      :headers="headers.org[deviceType]"
      :items="filteredAssetFaults"
      :loading="loading"
      :search="search"
      :footer-props="{
        itemsPerPageOptions: [25, 50, 100, -1],
      }"
      :items-per-page="-1"
      class="elevation-1 admin-table hardware-faults-report"
    >
      <template v-slot:progress>
        <v-progress-linear
          color="blue"
          height="2"
          indeterminate
        ></v-progress-linear>
      </template>

      <template v-slot:item.last_seen="{ item }">
        {{ formatDate(readValueFromPresenceMap(item, "last_seen")) }}
      </template>

      <template v-slot:item.battery_fault="{ item }">
        <v-icon
          v-if="checkForAssetFault(item, 'BatteryFault')"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.gps_fault="{ item }">
        <v-icon v-if="checkForAssetFault(item, 'GpsFault')" small color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.tamper_fault="{ item }">
        <v-icon
          v-if="checkForAssetFault(item, 'TamperFault')"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.connectivity_fault="{ item }">
        <v-icon
          v-if="checkForAssetFault(item, 'ConnectionFault')"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.faults_description="{ item }">
        {{ getActiveFaultsString(item) }}
      </template>
    </v-data-table>

    <v-data-table
      v-if="displayTable('Daily (All Orgs)') && deviceType == 'Camera System'"
      :headers="headers.daily[deviceType]"
      :items="filteredDailyReport"
      :loading="loading"
      :footer-props="{
        itemsPerPageOptions: [25, 50, 100, -1],
      }"
      :sort-by.sync="sortBy"
      :sort-desc.sync="descending"
      :items-per-page="-1"
      class="elevation-1"
    >
      <template v-slot:progress>
        <v-progress-linear
          color="blue"
          height="2"
          indeterminate
        ></v-progress-linear>
      </template>

      <!-- <template v-slot:item.storage="{ item }">
        <v-icon v-if="dailyReportFaults.storage.vehicles.has(item.vehicle_id)" small color="error">mdi-circle</v-icon>
      </template> -->

      <template v-slot:item.storage="{ item }">
        <v-icon
          v-if="dailyReportFaults.storage.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.camera="{ item }">
        <v-icon
          v-if="dailyReportFaults.camera.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.recording="{ item }">
        <v-icon
          v-if="dailyReportFaults.recording.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.gps="{ item }">
        <v-icon
          v-if="dailyReportFaults.gps.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.network="{ item }">
        <v-icon
          v-if="dailyReportFaults.status.vehicles.has(item.vehicle_id)"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.desc="{ item }">{{
        faultDescription(item.faults)
      }}</template>
    </v-data-table>

    <v-data-table
      v-if="displayTable('Daily (All Orgs)') && deviceType == 'Asset Tracker'"
      :headers="headers.daily[deviceType]"
      :items="filteredDailyReportAssetFaults"
      :loading="loading"
      :search="search"
      :footer-props="{
        itemsPerPageOptions: [25, 50, 100, -1],
      }"
      :items-per-page="-1"
      class="elevation-1 admin-table hardware-faults-report"
    >
      <template v-slot:progress>
        <v-progress-linear
          color="blue"
          height="2"
          indeterminate
        ></v-progress-linear>
      </template>

      <template v-slot:item.last_seen="{ item }">
        {{ formatDate(item.last_seen) }}
      </template>

      <template v-slot:item.battery_fault="{ item }">
        <v-icon
          v-if="checkForAssetFault(item, 'BatteryFault')"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.gps_fault="{ item }">
        <v-icon v-if="checkForAssetFault(item, 'GpsFault')" small color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.tamper_fault="{ item }">
        <v-icon
          v-if="checkForAssetFault(item, 'TamperFault')"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template v-slot:item.connectivity_fault="{ item }">
        <v-icon
          v-if="checkForAssetFault(item, 'ConnectionFault')"
          small
          color="error"
          >mdi-circle</v-icon
        >
      </template>

      <template>
      
      </template>
    </v-data-table>
    <br>

    <Pagination
      v-if="displayTable('Daily (All Orgs)') && deviceType == 'Camera System'"
      :pagination-info="pagination"
      :show-row-option="true"
      @updatedPage="setNewPagingInfo"
    />
  </div>
</template>

<script>
import { Logger } from "aws-amplify";
const logger = new Logger("Org Faults");

import {
  ListAccessibleOrgs,
  GetOrgFaults,
  GetFaultReport,
  getOrgAssetsFaults,
  getOrgAssetsPresense,
  getDailyFaultsAssets,
} from "@/services/API.js";

import { getCoor } from "@/utils";

import moment from "moment";
import xlsx from "xlsx";

import TableRefresh from "@/components/general/TableRefresh.vue";
import Pagination from "@/components/general/Pagination.vue";

const { CameraMatics } = window;

export default {
  name: "Faults",
  components: {
    TableRefresh,
    Pagination
  },
  data() {
    return {
      tab: 0,
      loading: false,
      search: "",
      selectedOrg: "",
      orgs: [],
      faults: [],
      assetFaults: [],
      presence: {},
      vehicles: [],
      errors: null,
      filters: ["storage", "camera", "recording", "gps", "status"],
      assetFilters: [
        {
          label: "battery",
          value: "BatteryFault",
        },
        {
          label: "connectivity",
          value: "ConnectionFault",
        },
        {
          label: "gps",
          value: "GpsFault",
        },
        {
          label: "tamper",
          value: "TamperFault",
        },
      ],

      selectedFilter: [],
      headers: {
        org: {
          "Camera System": [
            { text: "Vehicle", value: "vehicle_name", sortable: false },
            {
              text: "Storage",
              value: "storage",
              align: "center",
              sortable: false,
            },
            {
              text: "Video Loss",
              value: "camera",
              align: "center",
              sortable: false,
            },
            {
              text: "Not Recording",
              value: "recording",
              align: "center",
              sortable: false,
            },
            { text: "GPS", value: "gps", align: "center", sortable: false },
            {
              text: "Offline 7+ Days",
              value: "network",
              align: "center",
              sortable: false,
            },
            { text: "Description", value: "desc", sortable: false },
          ],
          "Asset Tracker": [
            {
              text: "Name",
              value: "asset_name",
              align: "left",
            },
            {
              text: "Last Seen",
              value: "last_seen",
              align: "left",
            },
            {
              text: "Battery",
              value: "battery_fault",
              align: "left",
            },
            {
              text: "GPS",
              value: "gps_fault",
              align: "left",
            },
            {
              text: "Tamper",
              value: "tamper_fault",
              align: "left",
            },
            {
              text: "Connectivity",
              value: "connectivity_fault",
              align: "left",
            },
            {
              text: "Description",
              value: "faults_description",
              align: "left",
            },
          ],
        },
        daily: {
          "Camera System": [
            { text: "Created On", value: "created_on", sortable: true },
            { text: "Organisation", value: "org_name", sortable: true },
            { text: "Vehicle", value: "vehicle_name", sortable: true },
            {
              text: "Storage",
              value: "storage",
              align: "center",
              sortable: false,
            },
            {
              text: "Video Loss",
              value: "camera",
              align: "center",
              sortable: false,
            },
            {
              text: "Not Recording",
              value: "recording",
              align: "center",
              sortable: false,
            },
            { text: "GPS", value: "gps", align: "center", sortable: false },
            {
              text: "Offline 7+ Days",
              value: "network",
              align: "center",
              sortable: false,
            },
            { text: "Description", value: "desc", sortable: false },
          ],
          "Asset Tracker": [
            {
              text: "Name",
              value: "asset_name",
              align: "left",
            },
            {
              text: "Last Seen",
              value: "last_seen",
              align: "left",
            },
            {
              text: "Battery",
              value: "battery_fault",
              align: "left",
            },
            {
              text: "GPS",
              value: "gps_fault",
              align: "left",
            },
            {
              text: "Tamper",
              value: "tamper_fault",
              align: "left",
            },
            {
              text: "Connectivity",
              value: "connectivity_fault",
              align: "left",
            },
            {
              text: "Description",
              value: "faultString",
              align: "left",
            },
          ],
        },
      },
      initFaults: {
        camera: {
          count: 0,
          vehicles: new Set(),
        },
        storage: {
          count: 0,
          vehicles: new Set(),
        },
        gps: {
          count: 0,
          vehicles: new Set(),
        },
        network: {
          count: 0,
          vehicles: new Set(),
        },
        recording: {
          count: 0,
          vehicles: new Set(),
        },
        status: {
          count: 0,
          vehicles: new Set(),
        },
      },
      faultPages: ["Per - Org", "Daily (All Orgs)"],
      selectedDates: [
        moment()
          .startOf("day")
          .format("YYYY-MM-DD"),
        moment()
          .endOf("day")
          .format("YYYY-MM-DD"),
      ],
      startValue: [
        moment()
          .startOf("day")
          .format("YYYY-MM-DD"),
        moment()
          .endOf("day")
          .format("YYYY-MM-DD"),
      ],
      selectedSingleDate: "",
      dailyReports: [],
      reportItems: [],
      dailyReportFaults: [],
      pagination: {
        pageNum: 1,
        pageCount: 1,
        rowsPerPage: 200
      },
      reportUrl: '',
      reportDate: '',
      dailyDownloadReportItems: [],

      dailyReportAssetFaults: [],
      sortBy: "org_name",
      descending: false,

      deviceType: "Camera System",
      types: ["Camera System", "Asset Tracker"],
    };
  },
  computed: {
    formatSelectedDates() {
      return [
        moment(this.selectedDates[0]).format("YYYY-MM-DD"),
        moment(this.selectedDates[1]).format("YYYY-MM-DD"),
      ];
    },
    orgName() {
      const orgs = this.orgs.map((org) => ({
        name: org.name,
      }));

      return orgs;
    },
    filteredItems() {
      const keywords = this.selectedFilter.map((index) => this.filters[index]);
      let results = [];

      for (const word in keywords) {
        var filter = keywords[word];

        for (const i in this.vehicles) {
          var vehicle = this.vehicles[i];
          var faults = vehicle.faults;

          for (const j in faults) {
            let fault = faults[j];

            if (fault.type.toLowerCase() == filter) {
              let index = results.indexOf(vehicle);

              if (index > -1) {
                // results.splice(index, 1);
              } else {
                results.push(vehicle);
              }
            }
          }
        }
      }

      if (keywords.length > 0) {
        return results;
      } else {
        return this.vehicles;
      }
    },

    filteredAssetFaults() {
      const filters = this.selectedFilter.map(
        (index) => this.assetFilters[index].value
      );

      return this.selectedFilter.length > 0
        ? this.getAssetsWithActiveFaults(this.assetFaults, filters)
        : this.assetFaults;
    },

    filteredDailyReportAssetFaults() {
      const filters = this.selectedFilter.map(
        (index) => this.assetFilters[index].value
      );

      return this.selectedFilter.length > 0
        ? this.getAssetsWithActiveFaults(this.dailyReportAssetFaults, filters)
        : this.dailyReportAssetFaults;
    },

    filteredDailyReport() {
      const keywords = this.selectedFilter.map(index =>
        this.filters[index].toLowerCase()
      );

      const hasSearch = this.search && this.search.trim().length > 0;

      // No search or filters - keep existing behaviour
      if (!hasSearch && keywords.length === 0) {
        const dataSet = this.reportItems.find(
          set => set.page == this.pagination.pageNum
        );

        return dataSet ? dataSet.data : [];
      }

      // Search/filter across every loaded page
      let data = this.reportItems.flatMap(page => page.data || []);

      if (hasSearch) {
        const query = this.search.trim().toLowerCase();
        data = data.filter(vehicle => vehicle.vehicle_name.trim().toLowerCase().includes(query));
      }

      if (keywords.length > 0) {
        const dataSet = this.reportItems.find(
          set => set.page == this.pagination.pageNum
        );
        if(Object.hasOwn(dataSet, 'data')) {
          data = dataSet.data.filter(vehicle =>
            (vehicle.faults || []).some(fault =>
              keywords.includes((fault.type || "").toLowerCase())
            )
          );
        } 
        else {
          data = []
        }
        
      }

      return data.sort((a, b) =>
        (a.org_name || "").localeCompare(b.org_name || "")
      );
    },

    // filteredDailyReport() {
    //   const keywords = this.selectedFilter.map((index) => this.filters[index]);
    //   let results = [];

    //   let dataSet = this.reportItems.find(set => set.page == this.pagination.pageNum) 

    //   if(!dataSet) return []

    //   for (const word in keywords) {
    //     var filter = keywords[word];

    //     for (const i in dataSet.data) {
    //       var vehicle = dataSet.data[i];
    //       var faults = vehicle.faults;

    //       for (const j in faults) {
    //         let fault = faults[j];

    //         if (fault.type.toLowerCase() == filter) {
    //           let index = results.indexOf(vehicle);

    //           if (index > -1) {
    //             // results.splice(index, 1);
    //           } else {
    //             results.push(vehicle);
    //           }
    //         }
    //       }
    //     }
    //   }

    //   if (keywords.length > 0) {
    //     return results.sort((a, b) => a.org_name.localeCompare(b.org_name));
    //   } else {
    //     return dataSet.data;
    //   }
    // },
    
    selectedPage() {
      switch (this.tab) {
        case 0:
          return "Per - Org";
        case 1:
          return "Daily (All Orgs)";
        default:
          return "Per - Org";
      }
    },
  },
  watch: {
    tab(val) {
      if (val == 1) {
        if (this.deviceType == "Camera System") {
          this.getDailyFaultsReport();
        } else {
          this.getAssetFaultsDaily();
        }
      }
    },
    selectedSingleDate() {
      this.selectedDates = [
        moment(this.selectedSingleDate)
          .startOf("day")
          .format("YYYY-MM-DD"),
        moment(this.selectedSingleDate)
          .endOf("day")
          .format("YYYY-MM-DD"),
      ];
    },

    selectedDates() {
      if (this.deviceType == "Camera System") {
        this.getDailyFaultsReport();
      } else {
        this.getAssetFaultsDaily(true);
      }
    },

    deviceType() {
      if (this.tab == 0) {
        if (this.selectedOrg.length > 0) {
          this.getFaultsWrapper();
        }
      }

      if (this.tab == 1) {
        if (this.deviceType == "Camera System") {
          this.getDailyFaultsReport();
        } else {
          this.getAssetFaultsDaily();
        }
      }
    },
  },
  mounted() {
    this.getAllOrgs();
  },
  methods: {
    filterByVehicleName(data, keyword) {
      const search = keyword.trim().toLowerCase();

      return data
        .map(page => ({
          ...page,
          data: page.data.filter(vehicle =>
            vehicle.vehicle_name.trim().toLowerCase().includes(search)
          )
        }))
        .filter(page => page.data.length > 0); // Remove empty pages (optional)
    },

    getActiveFaultsString(asset, report = false) {
      const batteryLevel = asset.faults.find(fault => fault.type == 'BatteryFault' && fault.status == true)
      const mapFaults = {
        BatteryFault: batteryLevel ? `Battery level ${batteryLevel.context.battery_level} mV` : ``,
        TamperFault: `Potential tampering`,
        ConnectionFault: `No contact, 7+ days`,
        GpsFault: `Last GPS fix failed`,
      };
      if (!report) {
        return asset.faults
          .filter((fault) => fault.status === true)
          .map((fault) => mapFaults[fault.type])
          .join(", ");
      } else {
        return asset.faults
          .filter((fault) => fault.status === true)
          .map((fault) => `[${mapFaults[fault.type]}]`)
          .join(", ");
      }
      
    },

    checkForAssetFault(item, type) {
      const fault = item.faults.find((fault) => fault.type == type);

      return fault ? JSON.parse(fault.status) : false;
    },

    readValueFromPresenceMap(item, val) {
      if (!item || !val) return "";

      if (!Object.hasOwn(this.presence, item.asset_id)) return "";

      return this.presence[item.asset_id][val];
    },

    formatDate(val) {
      if (!val) return "";
      return moment(val).format("DD MMM YYYY HH:mm:ss");
    },

    getAssetsWithActiveFaults(data, faultTypes) {
      return data.filter((asset) =>
        asset.faults.some(
          (fault) => faultTypes.includes(fault.type) && fault.status === true
        )
      );
    },

    async getDailyFaultsAssets() {
      this.loading = true;

      const faults = await getDailyFaultsAssets(this.formatSelectedDates);

      if (faults && Object.hasOwn(faults, "Reports")) {
        let reports = [];

        faults.Reports.forEach((item) => {
          reports.push({
            url: item.s3_link,
            created_on: item.created_on,
          });
        });

        reports.forEach((report) => {
          this.prepareAssetDailyReports(report.url, report.created_on);
        });
      }
    },

    async getAssetFaults(refresh = false) {
      this.loading = true;
      if (refresh) {
        this.assetFaults = [];
      }

      await Promise.all([
        this.getOrgAssetsFaults(),
        this.getOrgAssetsPresense(),
      ])
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    async getAssetFaultsDaily(refresh = false) {
      this.loading = true;
      if (refresh) {
        this.assetFaults = [];
        this.dailyReportAssetFaults = [];
      }

      await Promise.all([
        this.getDailyFaultsAssets(),
        this.getOrgAssetsPresense(),
      ])
        .then(() => {
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    findLocations(dataSet, propetyName = "location") {
      return new Promise(async (res, rej) => {
        try {
          let coords = dataSet.map((i, index) => {
            i.arrayPos = index;
            i.coords = {
              lat: getCoor(i.last_location.coords.lat),
              lng: getCoor(i.last_location.coords.long),
            };
            return i;
          });

          // we want no run all of these requests async so we map each request to a promise
          const promises = coords.map(
            (i) =>
              new Promise(async (res) => {
                try {
                  const location = await CameraMatics.HereMapService.multiRevGeocode(
                    [i.coords]
                  );
                  // if we can find the location
                  if (location.Response) {
                    coords[i.arrayPos][propetyName] =
                      location.Response.Item[0].Result[0].Location.Address.Label;
                    res(true);
                    // if we can't find the location
                  } else {
                    coords[i.arrayPos][propetyName] = "Unknown";
                    res(true);
                  }
                  // if something goes wrong, we just set the location as not known!
                } catch (exception) {
                  coords[i.arrayPos][propetyName] = "Unknown";
                  res(true);
                }
              })
          );

          // wait for all requests to finish the return the ammended set
          await Promise.all(promises);
          res(coords);
        } catch (ex) {
          logger.debug("error finding locations", ex);
          rej(false);
        }
      });
    },

    async getOrgAssetsPresense() {
      const id = this.retrunOrgID(this.selectedOrg);
      const presence = await getOrgAssetsPresense(id);
      const addLocations = await this.findLocations(presence);

      this.presence = addLocations.reduce((acc, obj) => {
        acc[obj.asset_id] = obj;
        return acc;
      }, {});

      logger.info("presence & locations", presence, addLocations);
    },

    async getOrgAssetsFaults() {
      const id = this.retrunOrgID(this.selectedOrg);
      const faults = await getOrgAssetsFaults(id);

      if (faults && Object.hasOwn(faults, "faults")) {
        this.assetFaults = faults.faults;
      }
    },

    displayTable(table) {
      return this.selectedPage == table ? true : false;
    },

    getAllOrgs() {
      ListAccessibleOrgs()
        .then((res) => {
          this.orgs = res.sort((a, b) => a.name.localeCompare(b.name));
        })
        .catch((err) => {
          this.errors = err;
        });
    },

    retrunOrgID(name) {
      let org = this.orgs.find((org) => {
        return org.name == name;
      });

      return org.id;
    },

    getFaultsByOrg() {
      this.devices = [];

      if (this.selectedOrg.length > 0) {
        this.loading = true;

        let id = this.retrunOrgID(this.selectedOrg);

        if (id) {
          GetOrgFaults(id)
            .then((res) => {
              if (res.Faults) {
                this.vehicles = res.Faults.filter((fa) => fa.faults.length > 0);
                this.faults = this.convertFaultsCount(res.Faults);

                this.loading = false;
              }
            })
            .catch((err) => {
              logger.info(err);
              this.loading = true;
            });
        }
      }
    },

    faultDescription(faults, asset = false) {
      let text = [];
      let faultObjs = this.convertFaultsToString(faults, asset);

      faultObjs.forEach((fault) => {
        text.push(fault.text);
      });

      return text.join("; ");
    },

    faultsMap(fault) {
      let map = {
        "dirty bit set": "Disk possibly corrupted",
      };

      // eslint-disable-next-line no-prototype-builtins
      return map.hasOwnProperty(fault) ? map[fault] : fault;
    },

    filterMap(filter) {
      let map = {
        storage: "Storage",
        camera: "Video Loss",
        recording: "Not Recording",
        gps: "GPS",
        status: "Offline 7+ Days",
        battery: "Battery",
        connectivity: "Connectivity",
        tamper: "Tamper",
      };

      return map[filter];
    },

    splitCamelCase(str) {
      const res = str
        .split(/(?=[A-Z])/)
        .map((char) => char.toLowerCase())
        .join(" ");

      return res.charAt(0).toUpperCase() + res.slice(1);
    },

    convertFaultsToString(faults, asset = false) {
      const res = new Map();
      faults.forEach((fault) => {
        let temp = null;
        let key = `${fault.type}-${fault.status}`;
        if (res.has(key)) temp = res.get(key);

        let value = temp ? temp.concat(fault.context) : [fault.context];
        res.set(key, value);
      });

      const result = [];
      res.forEach((value, key) => {
        let fault = key.split("-");
        let faultDetails = {
          type: "",
          text: ``,
        };
        
        switch (fault[0].toLowerCase()) {
          case "storage": {
            const disks = value.map((val) => `${val.medium} ${val.id}`);

            faultDetails.type = "storage";
            faultDetails.text = `${disks.join()} ${this.faultsMap(fault[1])}`;

            result.push(faultDetails);
            break;
          }

          case "camera": {
            faultDetails.type = "camera";
            faultDetails.text = `${this.splitCamelCase(
              fault[1]
            )} at channel ${value.map((val) => val.channel).join()}`;

            result.push(faultDetails);
            break;
          }
          case "gps": {
            faultDetails.type = "gps";
            faultDetails.text = "No GPS signal";

            result.push(faultDetails);
            break;
          }

          case "network": {
            faultDetails.type = "network";
            faultDetails.text = "No network";

            result.push(faultDetails);
            break;
          }

          case "recording": {
            faultDetails.type = "recording";
            faultDetails.text = `Not Recording at channel ${value
              .map((val) => val.channel)
              .join()}`;

            result.push(faultDetails);
            break;
          }

          case "status": {
            faultDetails.type = "status";
            faultDetails.text = `Offline 7+ days`;

            result.push(faultDetails);
            break;
          }

          case "batteryfault": {
            faultDetails.type = "battery";
            faultDetails.text = `Battery level ${this.readValueFromPresenceMap(asset, "battery_level")} mV`;

            result.push(faultDetails);
            break;
          }

          case "extpowerfault": {
            faultDetails.type = "connectivity";
            faultDetails.text = `No contact, 7+ days`;

            result.push(faultDetails);
            break;
          }

          case "tamperfault": {
            faultDetails.type = "tamper";
            faultDetails.text = `Potential Tampering`;

            result.push(faultDetails);
            break;
          }

          case "gpsfault": {
            faultDetails.type = "gps";
            faultDetails.text = `Last GPS fix failed`;

            result.push(faultDetails);
            break;
          }

          default:
            break;
        }
      });
      return result;
    },

    convertFaultsCount(faults) {
      logger.info('convertFaultsCount', faults)
      const response = faults.reduce((res, fa) => {
        if (fa.faults.length === 0) return res;
        let target = fa.faults[0];

        if (fa.faults[0].type) {
          target = fa.faults.reduce((res, fa) => {
            // The daily report returns GPS as all uppercase so I have to make sure it's lower case here so that both org and daily report filtering works
            fa.type = fa.type.toLowerCase();
            if (Number.isInteger(res[fa.type])) {
                res[fa.type]++;
            } else {
                res[fa.type] = 1;
            }
            return res;
          }, {});
        }

        Object.keys(target).forEach((name) => {
          let fixedName = name;

          if (res[fixedName]) {
            res[fixedName].count += target[fixedName];
            res[fixedName].vehicles.add(fa.vehicle_id);
          } else {
            logger.info("error : ", fixedName, res);
          }
        });
        return res;
      }, this.initFaults);

      return response;
    },

    // convertFaultsCount(faults) {
    //   const response = this.initFaults;

    //   for (const fault of faults) {
    //     const faultList = fault.faults;

    //     if (!faultList.length) {
    //       continue;
    //     }

    //     let target;

    //     // Daily report
    //     if (faultList[0].type) {
    //       target = {};

    //       for (const item of faultList) {
    //         const type = item.type.toLowerCase();
    //         target[type] = (target[type] || 0) + 1;
    //       }
    //     } else {
    //       // Organisation report
    //       target = faultList[0];
    //     }

    //     for (const name in target) {
    //       const entry = response[name];

    //       if (!entry) {
    //         logger.info("Unknown fault:", name);
    //         continue;
    //       }

    //       entry.count += target[name];
    //       entry.vehicles.add(fault.vehicle_id);
    //     }
    //   }

    //   return response;
    // },

    checkForFault(faults, type) {
      let fault = faults.find((fault) => {
        return fault.type == type;
      });

      return fault ? "FAULT" : "OK";
    },

    formatCSV(data, org = false) {
      logger.info('checkForFault', data)
      // Vehicle,Last Report Date,Last Report Time,GPS,Storage,Video Signal,Video Recording,Network,Info
      let mappedData = data.map((item) => ({
        ...(org && { Organisation: item.org_name }),
        ["Vehicle"]: item.vehicle_name,
        ["GPS"]: this.checkForFault(item.faults, "gps"),
        ["Storage"]: this.checkForFault(item.faults, "storage"),
        ["Video Signal"]: this.checkForFault(item.faults, "camera"),
        ["Video Recording"]: this.checkForFault(item.faults, "recording"),
        ["Network"]: this.checkForFault(item.faults, "status"),
        ["Info"]: this.formatFaultsInfo(item.faults),
      }));

      return mappedData;
    },

    formatFaultsInfo(faults) {
      let text = "";
      let faultObjs = this.convertFaultsToString(faults);

      faultObjs.forEach((fault) => {
        let faultString = `[${fault.text}] `.toString();

        text += faultString;
      });

      return text;
    },

    downloadFile() {
      if (this.tab == 0) {
        if (this.deviceType == "Camera System") {
          this.createOrgReportFile();
        } else {
          this.downloadOrgAssets()
        }
      } else if (this.tab == 1) {
        if (this.deviceType == "Camera System") {
          this.createDailyReportFile();
        } else {
          this.downloadOrgAssetsDaily()
        }
        
      }
    },

    getDailyFaultsReport() {
      this.loading = true;
      this.reportItems = [];

      GetFaultReport("daily_fault", this.formatSelectedDates)
        .then((res) => {
          this.loading = false;
          this.dailyReports = res;

          return res;
        })
        .then((res) => {
          let reports = [];

          res.forEach((item) => {
            reports.push({
              url: item.s3_link,
              created_on: item.created_on,
            });
          });

          reports.forEach((report) => {
            this.prepareReports(report.url, report.created_on);
          });
        })
        .catch((err) => {
          this.loading = false;
          logger.info("there was an error : ", err);
        });
    },
    
    prepareReports(url, date, page = 1, rowsPerPage = 200) {
      this.loading = true
      this.reportUrl = url 
      this.reportDate = date 
      
      this.reportItems = []
      this.dailyDownloadReportItems = []

      const createdOn = moment(date).format("YYYY-MM-DD");

      

      this.parseCSV(url)
        .then((items) => {
          logger.info('parsed csv', items)
          let pagedItems = []

          items.forEach((fault) => {
            const data =
              fault.faults.length > 0
                ? fault.faults.replace(/'/g, '"')
                : "[]";

            const jsonFaults = JSON.parse(data);

            fault.created_on = createdOn
            fault.faults = jsonFaults;
            fault.faultString = this.faultDescription(jsonFaults);

            pagedItems.push(fault);
          });

          const totalRows = pagedItems.length;
          const totalPages = Math.ceil(totalRows / rowsPerPage);

          for (let page = 1; page <= totalPages; page++) {
            const start = (page - 1) * rowsPerPage;
            const end = start + rowsPerPage;
            
            this.dailyReportFaults = this.convertFaultsCount(pagedItems.slice(start, end));

            this.reportItems.push({
              page,
              data: pagedItems.slice(start, end)
            });
            this.dailyDownloadReportItems = pagedItems
          }

          this.loading = false
          this.pagination.pageNum = page
          this.pagination.rowsPerPage = rowsPerPage
          this.pagination.pageCount = totalPages
        })
        .catch((err) => {
          logger.info("There was an error parsing the CSV ", err);
        });
    },

    setNewPagingInfo(page, rows) {
      this.pagination.pageNum = page

      if(rows != this.pagination.rowsPerPage) {
        this.pagination.rowsPerPage = rows
        this.prepareReports(this.reportUrl, this.reportDate, page, rows)
      }
      
    },

    prepareAssetDailyReports(url, date) {
      this.loading = true
      let formattedFaultArray = [];

      const convertToJson = (pythonStyleStr) => {
        return pythonStyleStr
          .replace(/'/g, '"') // Replace single quotes with double quotes
          .replace(/\bTrue\b/g, "true") // Replace True with true
          .replace(/\bFalse\b/g, "false"); // Replace False with false
      };

      this.parseCSV(url)
        .then((item) => {
          item.forEach((fault) => {
            let data = fault.faults.length > 0 ? fault.faults : [];

            const validJsonStr = convertToJson(data);
            const parsed = JSON.parse(validJsonStr);

            fault.created_on = moment(date).format("YYYY-MM-DD");
            fault.faults = parsed;
            fault.faultString = this.getActiveFaultsString(fault);

            formattedFaultArray.push(fault);
          });

          this.dailyReportAssetFaults = formattedFaultArray;
          this.loading = false
        })
        .catch((err) => {
          this.loading = false
          logger.info("There was an error parsing the CSV ", err);
        });
    },

    downloadOrgAssetsDaily() {
      this.excelLoading = true;

      const exportSheetName = `daily_faults_${moment().format("YYYY-MM-DD")}`;

      const headers = [
        "Organisation",
        "Name",
        "Battery",
        "GPS",
        "Tamper",
        "Connectivity",
        "Description"
      ];

      const faults = this.filteredDailyReportAssetFaults.map((item) => ({
        ...item,
        battery: this.checkForAssetFault(item, 'BatteryFault') ? 'FAULT' : 'OK',
        GPS: this.checkForAssetFault(item, 'GpsFault') ? 'FAULT' : 'OK',
        tamper: this.checkForAssetFault(item, 'TamperFault') ? 'FAULT' : 'OK',
        connectivity: this.checkForAssetFault(item, 'ConnectionFault') ? 'FAULT' : 'OK',
        description: this.getActiveFaultsString(item, true),
      }));

      const data = [
        headers,
        [],
        ...faults.map((item) => [
          item.org_name,
          item.asset_name,
          item.battery,
          item.GPS,
          item.tamper,
          item.connectivity,
          item.description
        ]),
      ];

      // Create a new workbook
      const wb = xlsx.utils.book_new();

      const ws = xlsx.utils.aoa_to_sheet(data);

      const colWidths = data[0].map((_, colIndex) => {
        const maxLength = data.reduce((max, row) => {
          const cellValue =
            row && row[colIndex] ? row[colIndex].toString() : "";
          return Math.max(max, cellValue.length);
        }, 0);
        return { width: maxLength + 2 }; // Add a little extra padding
      });

      // Set column widths in the worksheet
      ws["!cols"] = colWidths;

      // Append the worksheet to the workbook
      xlsx.utils.book_append_sheet(wb, ws, "Assets");

      // Write the workbook to a file (e.g., 'example.xlsx')
      xlsx.writeFile(wb, exportSheetName + ".xlsx");
      this.excelLoading = false;
    },

    downloadOrgAssets() {
      this.excelLoading = true;
      const exportSheetName = `${this.selectedOrg}_faults_${moment().format("YYYY-MM-DD_HH:mm:ss")}`;

      const headers = [
        "Name",
        "Battery",
        "GPS",
        "Tamper",
        "Connectivity",
        "Description"
      ];

      const faults = this.filteredAssetFaults.map((item) => ({
        ...item,
        battery: this.checkForAssetFault(item, 'BatteryFault') ? 'FAULT' : 'OK',
        GPS: this.checkForAssetFault(item, 'GpsFault') ? 'FAULT' : 'OK',
        tamper: this.checkForAssetFault(item, 'TamperFault') ? 'FAULT' : 'OK',
        connectivity: this.checkForAssetFault(item, 'ConnectionFault') ? 'FAULT' : 'OK',
        description: this.getActiveFaultsString(item, true)
      }));

      const data = [
        headers,
        [],
        ...faults.map((item) => [
          item.asset_name,
          item.battery,
          item.GPS,
          item.tamper,
          item.connectivity,
          item.description
        ]),
      ];

      // Create a new workbook
      const wb = xlsx.utils.book_new();

      const ws = xlsx.utils.aoa_to_sheet(data);

      const colWidths = data[0].map((_, colIndex) => {
        const maxLength = data.reduce((max, row) => {
          const cellValue =
            row && row[colIndex] ? row[colIndex].toString() : "";
          return Math.max(max, cellValue.length);
        }, 0);
        return { width: maxLength + 2 }; // Add a little extra padding
      });

      // Set column widths in the worksheet
      ws["!cols"] = colWidths;

      // Append the worksheet to the workbook
      xlsx.utils.book_append_sheet(wb, ws, "Assets");

      // Write the workbook to a file (e.g., 'example.xlsx')
      xlsx.writeFile(wb, exportSheetName + ".xlsx");
      this.excelLoading = false;
    },

    createOrgReportFile() {
      if (this.filteredItems.length > 0) {
        // create our excel file
        const wb = xlsx.utils.book_new();

        const formatData = this.formatCSV(this.vehicles);
        // create our worksheets
        const report = xlsx.utils.json_to_sheet(formatData);

        // apend them to the workbook
        xlsx.utils.book_append_sheet(wb, report, this.reportTitle);

        const reportName = `${this.selectedOrg}_faults_${moment().format(
          "YYYY-MM-DD HH:mm:ss"
        )}`;
        xlsx.writeFile(wb, reportName + ".xlsx");
      }
    },
    createDailyReportFile() {
      // create our excel file
      const wb = xlsx.utils.book_new();

      const formatData = this.formatCSV(this.dailyDownloadReportItems, true);
      // create our worksheets
      const report = xlsx.utils.json_to_sheet(formatData);

      // apend them to the workbook
      xlsx.utils.book_append_sheet(wb, report, this.reportTitle);

      const reportName = `daily_faults_report_${moment().format(
        "YYYY-MM-DD HH:mm:ss"
      )}`;
      xlsx.writeFile(wb, reportName + ".xlsx");
    },
    parseCSV(data) {
      let parsedData;

      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (res, reject) => {
        this.$papa.parse(data, {
          download: true,
          header: true,
          skipEmptyLines: true,
          delimiter: ",",
          complete: (results) => {
            parsedData = results.data;

            res(parsedData);
          },
          error: (error) => {
            reject(error);
          },
        });
      });
    },
    getFaultsWrapper() {
      if (this.deviceType == "Camera System") {
        this.getFaultsByOrg();
      } else {
        this.getAssetFaults();
      }
    },

    refreshWrapper() {
      if (this.tab == 0) {
        this.getFaultsWrapper();
      } else if (this.tab == 1) {
        if (this.deviceType == "Camera System") {
          this.getDailyFaultsReport();
        } else {
          this.getAssetFaultsDaily(true);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.hardware-faults {
  padding: 1em;
  border: 2px solid rgb(196, 196, 196);
  border-bottom: 0;
  position: relative;
  height: calc(100vh - 64px);
  overflow-y: scroll;
  overflow-x: visible; /* Hide horizontal scrollbar */

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #aaa;
    transition: all 0.2s;

    &:hover {
      background: #999;
    }
  }

  .faults-search-bar {
    padding-bottom: 2.5em;
  }

  .refresh-icon {
    top: inherit !important;
  }
  .download-container {
    position: absolute;
    right: 45px;
    top: inherit !important;
  }
}
</style>
